import type { ParsedDictationChunk } from '../createNvoqWebsocketFactory';
import { extractTextFromItem, isText, maybeEndOfSentence } from './textUtils';
import {
  NUMBERED_LIST_REGEX,
  startsWithCompleteDecimal,
  hasDecimalNumber,
} from './digitAndDecimalUtils';

// Map of similar phrases to numbered list commands - expand as needed
export const similarPhrasesToNumberedListMap = {
  'number won': '#1',
  'number one': '#1',
  'number too': '#2',
  'number to': '#2',
  'number free': '#3',
  'number tree': '#3',
  'number for': '#4',
  'number hive': '#5',
  'number sicks': '#6',
  'number ate': '#8',
  'free period': '3.',
  'one period': '1.',
  'to period': '2.',
  'for period': '4.',
} as const;

export type SimilarPhrase = keyof typeof similarPhrasesToNumberedListMap;

// Pre-compiled regex objects for performance optimization
// This avoids creating new regex objects on every function call
export const SIMILAR_PHRASE_REGEXES = new Map(
  Object.entries(similarPhrasesToNumberedListMap).map(([phrase, replacement]) => [
    phrase,
    { regex: new RegExp(`\\b${phrase}\\b`, 'gi'), replacement },
  ])
);

/**
 * Processes text to replace similar phrases with numbered list commands
 * Uses pre-compiled regex objects for better performance
 */
export function processTextForSimilarPhrasesToNumberedList(text: string): string {
  // Use pre-compiled regex objects for better performance
  let processedText = text;

  // Use pre-compiled regex objects instead of creating new ones each time
  for (const [, { regex, replacement }] of SIMILAR_PHRASE_REGEXES) {
    processedText = processedText.replace(regex, replacement);
  }

  return processedText;
}

/**
 * Checks if a dictation chunk contains a dynamic numbered list command
 * This function checks for decimal numbers first to prevent erroneous list creation
 * List creation should happen when:
 * 1. Pattern is at the start (index 0)
 * 2. Pattern is preceded by sentence ending punctuation and has text after
 */
export const isTextAndContainsDynamicNumberedListCommand = (
  item: ParsedDictationChunk
): boolean => {
  if (!isText(item) || !(item.payload as any).data?.text) {
    return false;
  }

  const text = extractTextFromItem(item);
  if (!text) return false;

  // For decimal detection, remove spaces to catch "1. 2" as potential "1.2"
  const textWithoutSpaces = text.replace(/ /g, '');

  // Checks for decimal numbers FIRST to prevent false positives
  // This catches patterns like "2.1 x 1.7 x 1.5 cm" which should not create lists
  if (startsWithCompleteDecimal(textWithoutSpaces)) {
    return false;
  }

  // Check numbered list regex with original text (preserves spaces like "1. first item")
  if (NUMBERED_LIST_REGEX.test(text)) {
    const match = NUMBERED_LIST_REGEX.exec(text);
    if (match) {
      const textBeforeMatch = text.slice(0, match.index);

      if (match.index === 0) {
        return true;
      }

      // Check if preceded by sentence-ending punctuation and allow both empty or non-empty text after
      // e.g. when in the first list item: "This is the first item. #2" or "This is the first item. #2 This is the second item"
      if (maybeEndOfSentence(textBeforeMatch)) {
        return true;
      }
    }
    return false;
  }

  // But first ensure the text doesn't contain any decimal number pattern anywhere
  // This prevents phrases like "one period" from matching when part of "2.1"
  // For example, "2.1 x 1.7 x 1.5 cm" should never trigger list creation
  if (hasDecimalNumber(textWithoutSpaces)) {
    return false;
  }

  // Check similar phrases last (least specific, most expensive)
  // but check for decimal numbers first to prevent false positives
  // e.g., "one period seven" should not match because "1.7" would be a decimal
  for (const phrase of Object.keys(similarPhrasesToNumberedListMap)) {
    if (text.includes(phrase)) {
      const replacement = similarPhrasesToNumberedListMap[phrase as SimilarPhrase];
      const resultText = text.replace(new RegExp(`\\b${phrase}\\b`, 'gi'), replacement);
      const resultWithoutSpaces = resultText.replace(/ /g, '');

      // If the replacement would create a decimal, don't trigger list creation
      if (hasDecimalNumber(resultWithoutSpaces)) {
        return false;
      }

      return true; // Found a valid similar phrase
    }
  }

  return false;
};
